import {
	noop,
	type QueryClient,
	queryOptions,
	useQuery,
	useSuspenseQuery,
} from "@tanstack/react-query"
import { friendKeys } from "../model/keys"
import type { Friend } from "../model/types"
import { getFriends } from "./client"

/** Shared config: the friend profile route loader warms the same query. */
export function friendsQueryOptions(userId: number) {
	return queryOptions({
		queryKey: friendKeys.list(userId),
		queryFn: () => getFriends(),
		staleTime: Number.POSITIVE_INFINITY,
	})
}

// The friend screens refresh a list older than this when they open.
const FRIENDS_REVALIDATE_MS = 10_000

/**
 * Loader read for the friend screens: the cached list now, a background refresh
 * when it is older than the window. No push tells us a friend changed their
 * search languages, so without it the cached friend can stay stale. `query()`
 * joins a running fetch (boot, reconnect) instead of cancelling it.
 */
export async function loadFriendsRevalidating(
	queryClient: QueryClient,
	userId: number,
	revalidate = true,
): Promise<Friend[]> {
	const options = friendsQueryOptions(userId)
	const friends = await queryClient.query({ ...options, staleTime: "static" })
	if (revalidate) {
		void queryClient
			.query({ ...options, staleTime: FRIENDS_REVALIDATE_MS })
			.catch(noop)
	}
	return friends
}

export function useFriends(userId: number) {
	return useQuery(friendsQueryOptions(userId))
}

export function useFriendsSuspense(userId: number) {
	return useSuspenseQuery(friendsQueryOptions(userId))
}
